import {
	AppstoreOutlined,
	ColumnHeightOutlined,
	UnorderedListOutlined,
	VerticalAlignMiddleOutlined,
} from "@ant-design/icons";
import { Button, Empty, Segmented } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import useReportDocumentOpener from "../../../hooks/useReportDocumentOpener";
import { useUserStore } from "../../../stores/userStore";
import {
	EMPTY_FINDING_FILTERS,
	FINDING_STATUS_CHIPS,
	FINDING_STATUS_LABELS,
	FindingFilters,
	FindingStageReport,
	FindingStatusChip,
	FindingStatusCounts,
	FindingTiming,
	areFindingOutcomesShown,
	getActiveStatusChip,
	getFindingResponsibleOptions,
	getFindingStatusCounts,
	getFindingTiming,
	getFindingTypeOptions,
	hasActiveFindingFilters,
	isReportFinished,
	matchesFindingFilters,
} from "../../../utils/reporting/findingFilters";
import { ReportItemKind } from "../../../utils/reporting/reportItemKinds";
import { collectResponsibleEmails } from "../../../utils/responsibleNames";
import ReportItemCardList from "./ReportItemCardList";
import { ReportItemEntry } from "./ReportItemParts";
import ReportItemRowList from "./ReportItemRowList";
import ReportItemsFilterBar from "./ReportItemsFilterBar";

type ItemsView = "cards" | "list";

// While statuses are hidden, no item shows how it stands against its
// deadline, lateness included.
const NO_TIMING: FindingTiming = { kind: "none" };

// The view each person last picked, kept in their browser for every report
// of the kind. Storage can be blocked: then it is Cards, and a pick isn't
// remembered.
const readStoredView = (key: string): ItemsView => {
	try {
		return localStorage.getItem(key) === "list" ? "list" : "cards";
	} catch {
		return "cards";
	}
};

const storeView = (key: string, view: ItemsView) => {
	try {
		localStorage.setItem(key, view);
	} catch {
		// Remembering the view is only a convenience.
	}
};

const VIEW_OPTIONS: {
	value: ItemsView;
	label: string;
	icon: JSX.Element;
}[] = [
	{ value: "cards", label: "Cards", icon: <AppstoreOutlined /> },
	{ value: "list", label: "List", icon: <UnorderedListOutlined /> },
];

// The progress bar's segments, in order. Overdue items are open ones past
// their deadline, so they are left out of In process and New.
const PROGRESS_SEGMENTS: {
	key: "completed" | "draft" | "overdue" | "pending";
	label: string;
}[] = [
	{ key: "completed", label: FINDING_STATUS_LABELS.completed.toLowerCase() },
	{ key: "draft", label: FINDING_STATUS_LABELS.draft.toLowerCase() },
	{ key: "overdue", label: "overdue" },
	{ key: "pending", label: FINDING_STATUS_LABELS.pending.toLowerCase() },
];

function ItemsProgress({
	counts,
	showStatus,
	kind,
}: Readonly<{
	counts: FindingStatusCounts;
	showStatus: boolean;
	kind: ReportItemKind;
}>) {
	const noun = counts.total === 1 ? kind.noun : kind.plural;

	if (!showStatus) {
		return (
			<div className="report-items__progress">
				<p className="report-items__progress-title">
					{counts.total} {noun}
				</p>
			</div>
		);
	}

	// Overdue items are counted in the legend only.
	const segments = PROGRESS_SEGMENTS.filter(({ key }) => counts[key] > 0);
	return (
		<div className="report-items__progress">
			<p className="report-items__progress-title">
				{counts.completed} of {counts.total} {noun} completed
			</p>
			<div className="report-items__bar" aria-hidden="true">
				{segments.map(({ key }) => (
					<span
						key={key}
						className={`report-items__bar-segment report-items__swatch--${key}`}
						style={{ flexGrow: counts[key] }}
					/>
				))}
			</div>
			<ul className="report-items__legend">
				{segments.map(({ key, label }) => (
					<li key={key}>
						<span
							className={`report-items__legend-dot report-items__swatch--${key}`}
						/>
						{counts[key]} {label}
					</li>
				))}
			</ul>
		</div>
	);
}

type ReportItemsPanelProps = {
	// Findings or follow-up actions: what the items are, and how to show them.
	kind: ReportItemKind;
	// The report's items, each with responsible_name resolved, and
	// completed_by_name where a completer is recorded.
	items: any[];
	// Lower-cased email to name, for the Responsible filter.
	emailToName: Record<string, string>;
	report: FindingStageReport & {
		operator?: string;
		folder_id?: string;
	};
	canEditItem?: (item: any) => boolean;
	onEditItem?: (index: number) => void;
	// Set for people who can edit the report: a report with no items offers to
	// add some.
	onAddItems?: () => void;
	// Shown when the report has no items, in place of the kind's text, such as
	// before an OFI's review; emptyHint adds a line under it.
	emptyText?: string;
	emptyHint?: string;
};

// The items on a report's page, an audit's findings or a report's follow-up
// actions: progress, filters, and the items as cards or as a list, whichever
// the person picked last.
function ReportItemsPanel({
	kind,
	items,
	emailToName,
	report,
	canEditItem,
	onEditItem,
	onAddItems,
	emptyText,
	emptyHint,
}: Readonly<ReportItemsPanelProps>) {
	const currentUserEmail = useUserStore((state) => state.user?.email);
	const [view, setView] = useState<ItemsView>(() =>
		readStoredView(kind.viewStorageKey)
	);
	const [filters, setFilters] = useState<FindingFilters>(EMPTY_FINDING_FILTERS);
	// The List view's open rows, by report position. A reload that removes an
	// item can move that to the next one, which only leaves a row open.
	const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
	const { openDocument, preview } = useReportDocumentOpener(
		report.operator ?? "",
		report.folder_id
	);

	const showStatus = areFindingOutcomesShown(
		report.status,
		report.open_status,
		report.is_rejected
	);
	// Deadlines only count while the report is out for action: before then the
	// work hasn't started, and once it is finished nothing is still due.
	const deadlinesLive =
		showStatus && !isReportFinished(report.status, report.open_status);
	const today = dayjs();

	const responsibleOptions = useMemo(
		() =>
			getFindingResponsibleOptions(
				collectResponsibleEmails(items),
				emailToName,
				currentUserEmail
			),
		[items, emailToName, currentUserEmail]
	);
	const typeOptions = useMemo(
		() => getFindingTypeOptions(items, kind),
		[items, kind]
	);
	// With one type or none, a type filter and type tags would tell nothing.
	const showType = typeOptions.length > 1;

	// A status or type filter the page no longer offers must not keep hiding
	// items.
	const activeFilters: FindingFilters = {
		...filters,
		statuses: showStatus
			? filters.statuses.filter(
					(status) => deadlinesLive || status !== "overdue"
				)
			: [],
		types: showType ? filters.types : [],
	};
	const filtersActive = hasActiveFindingFilters(activeFilters);
	const counts = getFindingStatusCounts(items, today, deadlinesLive);

	const entries: ReportItemEntry[] = items
		.map((item, index) => ({
			item,
			index,
			timing: showStatus
				? getFindingTiming(item, today, deadlinesLive)
				: NO_TIMING,
		}))
		.filter(({ item, index }) =>
			matchesFindingFilters(item, index, activeFilters, kind, today)
		);

	const chipCounts: Record<FindingStatusChip, number> = {
		all: counts.total,
		open: counts.open,
		overdue: counts.overdue,
		completed: counts.completed,
	};
	const chipOptions = FINDING_STATUS_CHIPS.filter(
		(chip) => deadlinesLive || chip.key !== "overdue"
	).map((chip) => ({
		value: chip.key,
		label: (
			<span className="report-items__chip">
				{chip.label}
				<span className="report-items__chip-count">{chipCounts[chip.key]}</span>
			</span>
		),
	}));

	const changeStatusChip = (key: FindingStatusChip) =>
		setFilters((prev) => ({
			...prev,
			statuses:
				FINDING_STATUS_CHIPS.find((chip) => chip.key === key)?.statuses ?? [],
		}));
	const clearFilters = () => setFilters(EMPTY_FINDING_FILTERS);

	const changeView = (next: ItemsView) => {
		setView(next);
		storeView(kind.viewStorageKey, next);
	};

	// With filters on, Expand all and Collapse all only touch the items on
	// screen.
	const visibleIndexes = entries.map((entry) => entry.index);
	const allExpanded =
		visibleIndexes.length > 0 &&
		visibleIndexes.every((index) => expanded.has(index));
	const toggleAll = () =>
		setExpanded((prev) => {
			const next = new Set(prev);
			for (const index of visibleIndexes) {
				if (allExpanded) next.delete(index);
				else next.add(index);
			}
			return next;
		});
	const toggleRow = (index: number) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});

	if (items.length === 0) {
		const text = emptyText ?? kind.emptyText;
		return (
			<section className="report-items" aria-label={kind.label}>
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						emptyHint ? (
							<>
								{text}
								<span className="report-items__empty-hint">{emptyHint}</span>
							</>
						) : (
							text
						)
					}
				>
					{onAddItems && (
						<Button onClick={onAddItems}>{`Add ${kind.plural}`}</Button>
					)}
				</Empty>
			</section>
		);
	}

	const renderItems = () => {
		if (entries.length === 0) {
			return (
				<div className="report-items__no-match">
					<p className="report-items__muted">
						{`No ${kind.plural} match these filters.`}
					</p>
					<Button onClick={clearFilters}>Clear filters</Button>
				</div>
			);
		}

		const viewProps = {
			kind,
			entries,
			showStatus,
			showType,
			stageReport: report,
			canEditItem,
			onEditItem,
			onOpenDocument: openDocument,
		};
		return view === "list" ? (
			<ReportItemRowList
				{...viewProps}
				expanded={expanded}
				onToggle={toggleRow}
			/>
		) : (
			<ReportItemCardList {...viewProps} />
		);
	};

	return (
		<section className="report-items" aria-label={kind.label}>
			<div className="report-items__toolbar">
				<div className="report-items__toolbar-top">
					<ItemsProgress counts={counts} showStatus={showStatus} kind={kind} />
					<div className="report-items__view-controls">
						{view === "list" && (
							<Button
								icon={
									allExpanded ? (
										<VerticalAlignMiddleOutlined />
									) : (
										<ColumnHeightOutlined />
									)
								}
								disabled={entries.length === 0}
								onClick={toggleAll}
							>
								{allExpanded ? "Collapse all" : "Expand all"}
							</Button>
						)}
						<Segmented<ItemsView>
							aria-label={`View ${kind.plural} as`}
							value={view}
							onChange={changeView}
							options={VIEW_OPTIONS}
						/>
					</div>
				</div>
				<div className="report-items__filters">
					{showStatus && (
						<Segmented<FindingStatusChip>
							aria-label="Filter by status"
							value={getActiveStatusChip(activeFilters.statuses) ?? "all"}
							onChange={changeStatusChip}
							options={chipOptions}
						/>
					)}
					<div className="report-items__filter-bar">
						<ReportItemsFilterBar
							kind={kind}
							filters={filters}
							onChange={setFilters}
							showStatusFilter={false}
							responsibleOptions={responsibleOptions}
							typeOptions={showType ? typeOptions : []}
						/>
					</div>
				</div>
				{/* With no match, the message below has its own Clear filters. */}
				{filtersActive && entries.length > 0 && (
					<div className="report-items__results">
						<span>
							Showing {entries.length} of {items.length}
						</span>
						<span aria-hidden="true">·</span>
						<Button
							className="p-0! h-auto! text-sm"
							type="link"
							onClick={clearFilters}
						>
							Clear filters
						</Button>
					</div>
				)}
			</div>
			{renderItems()}
			{preview}
		</section>
	);
}

export default ReportItemsPanel;
